/**
 * Create a reusable API client with a CSRF token baked in.
 *
 * @param {string} csrf - The CSRF token to include in every request.
 * @returns {(url: string, method: string, body?: any, extraHeaders?: Record<string, string>) => Promise<any>} An async function that performs API requests.
 */
export function createApiClient(csrf) {
    /**
     * Perform an API request.
     *
     * @param {string} url - The URL to fetch.
     * @param {string} method - The HTTP method (GET, POST, etc.).
     * @param {any} [body=null] - The request body (sent as JSON for POST requests).
     * @param {Record<string, string>} [extraHeaders={}] - Additional headers to merge.
     * @returns {Promise<any>} The parsed JSON response.
     * @throws {Error} If the response is not OK.
     */
    const apiRequest = async function (
        url,
        method,
        body = null,
        extraHeaders = {},
    ) {
        const headers = {
            "Content-Type": "text/html",
            "X-CSRF-Token": csrf,
            ...extraHeaders,
        };

        /** @type {RequestInit} */
        const options = { method, headers };
        if (method === "POST" && body) {
            options.body = JSON.stringify(body);
        }

        const response = await fetch(url, options);
        if (!response.ok) {
            throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return await response.json();
    };

    return apiRequest;
}
